import { ICON_KINDS, type LinkIcon, type LinkOpenIn } from '@flame/shared';
import { useState } from 'preact/hooks';
import { Api, ApiError, errorMessage, type Link, type Section } from '../api';
import { loadPage, toast } from '../state';
import { LinkIconView } from './Icon';
import { Field, Modal } from './ui';

type IconChoice = 'auto' | 'none' | (typeof ICON_KINDS)[number];

function hostOf(url: string): string | null {
  try {
    return new URL(/^[a-z][\w+.-]*:/i.test(url) ? url : `https://${url}`).host || null;
  } catch {
    return null;
  }
}

/** Add a link (no `link`) or edit one. */
export function LinkEditor({
  link,
  sections,
  defaultSectionId,
  onClose,
}: {
  link?: Link;
  sections: Section[];
  defaultSectionId?: string;
  onClose: () => void;
}) {
  const [url, setUrl] = useState(link?.url ?? '');
  const [title, setTitle] = useState(link?.title ?? '');
  const [description, setDescription] = useState(link?.description ?? '');
  const [sectionId, setSectionId] = useState(link?.sectionId ?? defaultSectionId ?? '');
  const [tags, setTags] = useState(link?.tags.join(', ') ?? '');
  const [pinned, setPinned] = useState(link?.pinned ?? false);
  const [openIn, setOpenIn] = useState<LinkOpenIn>(link?.openIn ?? 'default');
  const [iconKind, setIconKind] = useState<IconChoice>(
    !link ? 'auto' : link.icon ? (link.icon.kind === 'favicon' ? 'auto' : link.icon.kind) : 'none'
  );
  const [iconValue, setIconValue] = useState(
    link?.icon && link.icon.kind !== 'favicon' ? link.icon.value : ''
  );
  const [busy, setBusy] = useState(false);

  const host = hostOf(url);
  const icon: LinkIcon | null =
    iconKind === 'none'
      ? null
      : iconKind === 'auto' || iconKind === 'favicon'
        ? host
          ? { kind: 'favicon', value: host }
          : null
        : iconValue.trim()
          ? { kind: iconKind, value: iconValue.trim() }
          : null;

  async function submit(e: Event) {
    e.preventDefault();
    setBusy(true);
    const body = {
      url,
      title: title.trim() || undefined,
      description: description.trim(),
      sectionId: sectionId || undefined,
      tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
      pinned,
      openIn,
      // On create, leave the icon to the server unless one was chosen.
      icon: !link && iconKind === 'auto' ? undefined : icon,
    };
    try {
      if (link) await Api.updateLink(link.id, { ...body, title: body.title ?? link.title });
      else await Api.createLink(body);
      await loadPage();
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'duplicate_url') {
        const existing = (err.body as { link?: Link }).link;
        toast(`Already saved as "${existing?.title ?? url}".`, 'error');
      } else {
        toast(errorMessage(err), 'error');
      }
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!link || !confirm(`Delete "${link.title}"?`)) return;
    try {
      await Api.deleteLink(link.id);
      await loadPage();
      onClose();
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  return (
    <Modal title={link ? 'Edit link' : 'Add link'} onClose={onClose}>
      <form class="form" onSubmit={submit}>
        <Field label="URL">
          <input
            required
            autoFocus
            value={url}
            onInput={(e) => setUrl(e.currentTarget.value)}
            placeholder="https://example.com"
            inputMode="url"
            autoComplete="off"
          />
        </Field>
        <Field label="Title" hint={!link ? 'Leave blank to use the page title.' : undefined}>
          <input value={title} onInput={(e) => setTitle(e.currentTarget.value)} maxLength={256} />
        </Field>
        <Field label="Description">
          <input
            value={description}
            onInput={(e) => setDescription(e.currentTarget.value)}
            maxLength={1000}
          />
        </Field>
        <div class="field-row">
          <Field label="Section">
            <select value={sectionId} onChange={(e) => setSectionId(e.currentTarget.value)}>
              {!link && <option value="">Inbox</option>}
              {sections.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Open in">
            <select value={openIn} onChange={(e) => setOpenIn(e.currentTarget.value as LinkOpenIn)}>
              <option value="default">Default</option>
              <option value="same_tab">Same tab</option>
              <option value="new_tab">New tab</option>
            </select>
          </Field>
        </div>
        <Field label="Tags" hint="Comma separated, used by search.">
          <input value={tags} onInput={(e) => setTags(e.currentTarget.value)} />
        </Field>
        <div class="field-row">
          <Field label="Icon">
            <select
              value={iconKind}
              onChange={(e) => setIconKind(e.currentTarget.value as IconChoice)}
            >
              <option value="auto">Site icon</option>
              <option value="mdi">Material Design icon</option>
              <option value="url">Image URL</option>
              <option value="none">Letter</option>
            </select>
          </Field>
          {(iconKind === 'mdi' || iconKind === 'url') && (
            <Field
              label={iconKind === 'mdi' ? 'Icon name' : 'Image URL'}
              hint={
                iconKind === 'mdi' ? (
                  <>
                    e.g. <code>github</code>, from{' '}
                    <a href="https://pictogrammers.com/library/mdi/" target="_blank" rel="noreferrer">
                      the MDI library
                    </a>
                  </>
                ) : undefined
              }
            >
              <input value={iconValue} onInput={(e) => setIconValue(e.currentTarget.value)} />
            </Field>
          )}
          <div class="icon-preview" aria-label="Icon preview">
            <LinkIconView icon={icon} title={title || host || '?'} size={32} />
          </div>
        </div>
        <label class="check">
          <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.currentTarget.checked)} />
          Pin to the top of the page
        </label>
        <footer class="form-actions">
          {link && (
            <button type="button" class="button button-danger" onClick={remove}>
              Delete
            </button>
          )}
          <span class="spacer" />
          <button type="button" class="button" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" class="button button-primary" disabled={busy}>
            {busy ? 'Saving…' : link ? 'Save' : 'Add link'}
          </button>
        </footer>
      </form>
    </Modal>
  );
}
